import { add, differenceInDays } from 'date-fns'
import { Types } from 'service-api'
import { getTzDateInUtc } from './dates'

export type BillingCycle = 'WEEKLY' | 'FOUR_WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY'

export const generateBenefitDisplayName = (benefit: Types.BenefitAdminDto) => {
  return benefit.benefitOffers
    ?.map((benefitOffer) => benefitOffer.offer?.displayName)
    .filter((name) => !!name?.trim())
    .join(', ')
}

export const membershipDisplayName = (locationMembership: Types.LocationMembershipAdminDto | null | undefined) =>
  locationMembership?.displayName ||
  locationMembership?.membership?.displayName ||
  locationMembership?.name ||
  locationMembership?.membership?.name ||
  ''

export const findLocationUserProfileMembership = (
  locationUserProfileMemberships: Types.LocationUserProfileMembershipAdminDto[] | null | undefined,
  isAdmin?: boolean
) =>
  locationUserProfileMemberships?.find(
    (locationUserProfileMembership) =>
      locationUserProfileMembership?.subStatus === 'ACTIVE' &&
      (isAdmin ? locationUserProfileMembership?.status === 'ACTIVE' : true)
  ) ||
  locationUserProfileMemberships?.find(
    (locationUserProfileMembership) =>
      locationUserProfileMembership?.subStatus === 'PAUSED' &&
      (isAdmin ? locationUserProfileMembership?.status === 'ACTIVE' : true)
  ) ||
  locationUserProfileMemberships
    ?.filter(
      (locationUserProfileMembership) =>
        locationUserProfileMembership?.subStatus === 'CANCELED' &&
        (isAdmin ? locationUserProfileMembership?.status === 'ACTIVE' : true)
    )
    ?.sort((a, b) => {
      const dateA = new Date(a.createdAt || 0)
      const dateB = new Date(b.createdAt || 0)
      return dateB.getTime() - dateA.getTime()
    })?.[0]

const getCycleDurationInDays = (billingCycle: BillingCycle | undefined): number | null => {
  switch (billingCycle) {
    case 'WEEKLY':
      return 7
    case 'FOUR_WEEKLY':
      return 28
    case 'MONTHLY':
      return 30
    case 'QUARTERLY':
      return 90
    case 'ANNUALLY':
      return 365
    default:
      return null
  }
}

const calculateActionDate = (startDate: Date, billingCycle: BillingCycle | undefined, direction: 1 | -1) => {
  if (!startDate) {
    return startDate
  }
  const cycleDuration = getCycleDurationInDays(billingCycle)
  if (cycleDuration === null) {
    return startDate
  }
  return add(startDate, { days: direction * cycleDuration })
}

export const nextActionDate = (startDate: Date, billingCycle: BillingCycle | undefined) => {
  return calculateActionDate(startDate, billingCycle, 1)
}

export const prevActionDate = (startDate: Date, billingCycle: BillingCycle | undefined) => {
  return calculateActionDate(startDate, billingCycle, -1)
}

export const membershipCycle = (
  locationUserProfileMembership: Types.LocationUserProfileMembershipAdminDto | null | undefined,
  billingCycle: BillingCycle | undefined,
  timezone: string
) => {
  const nextActionDateUTC = getTzDateInUtc(locationUserProfileMembership?.nextActionDate, null, timezone)
  return {
    startDate: prevActionDate(nextActionDateUTC, billingCycle),
    endDate: add(nextActionDateUTC, { days: -1 }),
  }
}

// This gets the membership cycle which a certain date falls into
export const membershipCycleForDate = (
  locationUserProfileMembership: Types.LocationUserProfileMembershipAdminDto | null | undefined,
  billingCycle: BillingCycle | undefined,
  timezone: string,
  date: Date
) => {
  const startDateUTC = getTzDateInUtc(locationUserProfileMembership?.startDate, null, timezone)
  if (date < startDateUTC) {
    return null
  }

  const cycleDurationInDays = getCycleDurationInDays(billingCycle)
  if (cycleDurationInDays === null) {
    return null
  }

  const daysSinceMembershipStart = differenceInDays(date, startDateUTC)
  const completeCycles = Math.floor(daysSinceMembershipStart / cycleDurationInDays)
  const cycleStartDate = add(startDateUTC, {
    days: completeCycles * cycleDurationInDays,
  })
  const cycleEndDate = add(cycleStartDate, {
    days: cycleDurationInDays,
  })
  return {
    startDate: cycleStartDate,
    // in the UI we show the endDate inclusive, so we need to subtract 1 day from the endDate
    endDate: add(cycleEndDate, { days: -1 }),
  }
}

export const findMembershipService = (locationServiceTypes?: Types.LocationServiceTypeAdminDto[]) => {
  return locationServiceTypes?.find(({ serviceType }) => serviceType?.name === 'memberships')
}

export const checkForActiveMembershipService = (locationServiceTypes?: Types.LocationServiceTypeAdminDto[]) => {
  return findMembershipService(locationServiceTypes)?.serviceType?.status === 'ACTIVE'
}
